import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";

/**
 * Shared 3-card pricing comparison.
 *
 * Two usage modes:
 *   1. Navigate mode (default — Pricing page, How It Works): clicking a CTA
 *      runs the provided callback (typically routing to /profile?step=subscription).
 *      `selectedTier` is undefined so no selection highlight is shown.
 *   2. Select mode (Onboarding step, My Plan tab): pass `selectedTier` and
 *      the onSelect* callbacks set state in the parent. The whole card is
 *      clickable; the matching card gets a highlighted border.
 *
 * `recommendedTier` (default "yearly") adds a yellow glow on that card even
 * when nothing is selected yet — a visual hint, independent of selection.
 *
 * `hideHeading` suppresses the "Tutor Plans" title for surfaces (like the
 * onboarding step) that already render their own heading.
 *
 * All props are optional from TypeScript's point of view — JSDoc here so
 * consumers in .tsx files don't get spurious "missing required prop" errors.
 *
 * @param {{
 *   hideHeading?: boolean,
 *   hideBasic?: boolean,
 *   isLoggedInBasic?: boolean,
 *   selectedTier?: string,
 *   recommendedTier?: string,
 *   onSelectBasic?: () => void,
 *   onSelectMonthly?: () => void,
 *   onSelectYearly?: () => void,
 *   basicCtaLabel?: string,
 *   monthlyCtaLabel?: string,
 *   yearlyCtaLabel?: string,
 * }} props
 */
const PricingComparison = (props) => {
  const {
    hideHeading = false,
    hideBasic = false,
    isLoggedInBasic = false,
    selectedTier,
    recommendedTier = "yearly",
    onSelectBasic,
    onSelectMonthly,
    onSelectYearly,
    basicCtaLabel,
    monthlyCtaLabel = "Choose monthly",
    yearlyCtaLabel = "Choose yearly",
  } = props;
  // The two Premium cards live inside a shared dashed-border wrapper that
  // groups them visually as "the Premium plan — choose monthly or yearly
  // billing". The wrapper takes md=8 next to Basic (or md=12 when Basic is
  // hidden), and the two Premium cards always split that space 50/50.
  const premiumWrapperMd = hideBasic ? 12 : 8;
  const premiumGridMd = 6;
  const resolvedBasicCta =
    basicCtaLabel || (isLoggedInBasic ? "Current free plan" : "Get started");
  const basicDisabled = isLoggedInBasic && !onSelectBasic;

  // Border colours
  const greyBorder = "1px solid #e2e8f0";
  const selectedBorder = "2px solid #712d94";
  // Yellow glow used for both "recommended" hint and the selected state on yearly
  const recommendYellowGlow = "0 0 0 3px rgba(255, 193, 7, 0.6)";

  return (
    <Box>
      {!hideHeading && (
        <Box sx={{ mb: { xs: 4, md: 6 }, textAlign: "center" }}>
          <Typography
            sx={{
              fontWeight: 800,
              color: "#1e293b",
              mb: 1,
              fontSize: { xs: "2rem", md: "3rem" },
              lineHeight: 1.1,
              textAlign: "center",
            }}
          >
            Tutor Plans
          </Typography>
          <Typography sx={{ color: "#6b7280", fontSize: "1rem", maxWidth: 720, mx: "auto", textAlign: "center" }}>
            Choose a simple package. Premium tutors can advertise courses and get better visibility.
          </Typography>
        </Box>
      )}

      <Grid container spacing={3} alignItems="stretch">
        {/* Basic — hidden in some surfaces (e.g. the My Plan tab when the
            tutor is already on an active Premium subscription). */}
        {!hideBasic && (
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            onClick={onSelectBasic}
            sx={{
              border: selectedTier === "basic" ? selectedBorder : greyBorder,
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              cursor: onSelectBasic ? "pointer" : "default",
              transition: "all 0.2s ease",
              backgroundColor:
                selectedTier === "basic" ? "rgba(113, 45, 148, 0.04)" : "#ffffff",
              "&:hover": onSelectBasic
                ? {
                    borderColor: "#712d94",
                    boxShadow: "0 4px 12px rgba(113, 45, 148, 0.15)",
                    transform: "translateY(-2px)",
                  }
                : {},
            }}
          >
            <CardContent sx={{ p: 4, display: "flex", flexDirection: "column", flexGrow: 1 }}>
              <Typography
                sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 1.2, fontSize: "0.875rem", textTransform: "uppercase", lineHeight: 1.4 }}
              >
                BASIC
              </Typography>
              <Typography
                sx={{ fontWeight: 800, color: "#1e293b", mt: 1, lineHeight: 1.1, fontSize: "2rem" }}
              >
                Free
              </Typography>
              <Typography sx={{ color: "#64748b", mt: 0.5, fontSize: "0.95rem" }}>
                Good for getting listed
              </Typography>
              <FeatureList
                items={[
                  "Name",
                  "Subjects taught",
                  "Bio / About section",
                  "Profile photo",
                  "Basic listing in directory",
                  "Receive and reply to enquiries",
                ]}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                fullWidth
                disabled={basicDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectBasic?.();
                }}
                variant="outlined"
                sx={{
                  mt: 3,
                  borderColor: "#e2e8f0",
                  color: "#64748b",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  py: 1.1,
                  "&.Mui-disabled": { borderColor: "#e2e8f0", color: "#94a3b8" },
                }}
              >
                {resolvedBasicCta}
              </Button>
            </CardContent>
          </Card>
        </Grid>
        )}

        {/* Premium plan wrapper — visually groups the Monthly and Yearly
            cards as two cadences of the same Premium plan. Dashed light
            purple border + a small heading "legend" overlapping the top
            border, fieldset-style. Background colour on the heading
            assumes a white surface (true for all four current callers:
            /pricing, How It Works, onboarding Plan step, MyPlanTab). */}
        <Grid item xs={12} md={premiumWrapperMd}>
          <Box
            sx={{
              position: 'relative',
              border: '1px dashed #d6c2e4',
              borderRadius: 3,
              p: { xs: 1.5, md: 2 },
              pt: { xs: 2.5, md: 2.5 },
              height: '100%',
            }}
          >
            <Typography
              sx={{
                position: 'absolute',
                top: -10,
                left: { xs: 16, md: 24 },
                backgroundColor: '#fff',
                px: 1,
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#712d94',
                lineHeight: 1.2,
              }}
            >
              Premium plan — choose monthly or yearly billing
            </Typography>
            <Grid container spacing={3} alignItems="stretch" sx={{ height: '100%' }}>

        {/* Premium Monthly */}
        <Grid item xs={12} md={premiumGridMd}>
          <Card
            elevation={0}
            onClick={onSelectMonthly}
            sx={{
              border: selectedTier === "monthly" ? selectedBorder : greyBorder,
              borderRadius: 3,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              cursor: onSelectMonthly ? "pointer" : "default",
              transition: "all 0.2s ease",
              backgroundColor:
                selectedTier === "monthly" ? "rgba(113, 45, 148, 0.04)" : "#ffffff",
              "&:hover": onSelectMonthly
                ? {
                    borderColor: "#712d94",
                    boxShadow: "0 4px 16px rgba(113, 45, 148, 0.2)",
                    transform: "translateY(-2px)",
                  }
                : {},
            }}
          >
            <CardContent sx={{ p: 4, display: "flex", flexDirection: "column", flexGrow: 1 }}>
              <Typography
                sx={{ color: "#712d94", fontWeight: 700, letterSpacing: 1.2, fontSize: "0.875rem", textTransform: "uppercase", lineHeight: 1.4 }}
              >
                PREMIUM MONTHLY
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1 }}>
                <Typography sx={{ fontWeight: 800, color: "#1e293b", lineHeight: 1.1, fontSize: "2rem" }}>
                  £3.99
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: "1rem" }}>
                  / month
                </Typography>
              </Stack>
              <Typography sx={{ color: "#64748b", mt: 0.5, fontSize: "0.95rem" }}>
                Flexible monthly plan — cancel anytime
              </Typography>
              <FeatureList
                items={[
                  "2 course adverts per year",
                  "Best for serious tutors",
                  "Website",
                  "Phone number",
                  "Social media",
                  "Featured badge",
                  "Higher search ranking",
                  "Greater profile visibility",
                ]}
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectMonthly?.();
                }}
                variant="outlined"
                sx={{
                  mt: 3,
                  borderColor: "#712d94",
                  color: "#712d94",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  py: 1.1,
                  "&:hover": {
                    borderColor: "#5a2476",
                    backgroundColor: "rgba(113,45,148,0.04)",
                  },
                }}
              >
                {monthlyCtaLabel}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Premium Yearly */}
        <Grid item xs={12} md={premiumGridMd}>
          <Card
            elevation={0}
            onClick={onSelectYearly}
            sx={{
              backgroundColor: "#712d94",
              color: "#fff",
              borderRadius: 3,
              height: "100%",
              position: "relative",
              overflow: "visible",
              display: "flex",
              flexDirection: "column",
              cursor: onSelectYearly ? "pointer" : "default",
              transition: "all 0.2s ease",
              // Yellow glow when either explicitly selected OR shown as the
              // recommended option (visual hint before the user has chosen).
              boxShadow:
                selectedTier === "yearly" || (!selectedTier && recommendedTier === "yearly")
                  ? recommendYellowGlow
                  : "none",
              "&:hover": onSelectYearly
                ? {
                    boxShadow: "0 4px 16px rgba(113, 45, 148, 0.35)",
                    transform: "translateY(-2px)",
                  }
                : {},
            }}
          >
            <Chip
              label="Best Value"
              sx={{
                position: "absolute",
                top: -12,
                right: 16,
                backgroundColor: "#FFC107",
                color: "#000",
                fontWeight: 700,
                fontSize: "0.75rem",
                height: 32,
                zIndex: 1,
                "& .MuiChip-label": { px: 1.5 },
              }}
            />
            <CardContent sx={{ p: 4, display: "flex", flexDirection: "column", flexGrow: 1 }}>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  fontSize: "0.875rem",
                  textTransform: "uppercase",
                  lineHeight: 1.4,
                }}
              >
                PREMIUM YEARLY
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 1 }}>
                <Typography sx={{ fontWeight: 800, color: "#fff", lineHeight: 1.1, fontSize: "2rem" }}>
                  £34.99
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "1rem" }}>
                  / year
                </Typography>
              </Stack>
              <Typography sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5, fontSize: "0.95rem" }}>
                Equivalent to £2.92/month
              </Typography>
              <FeatureList
                items={[
                  "Save 27% vs monthly",
                  "2 course adverts per year",
                  "Best for serious tutors",
                  "Website",
                  "Phone number",
                  "Social media",
                  "Featured badge",
                  "Higher search ranking",
                  "Greater profile visibility",
                ]}
                tone="dark"
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                fullWidth
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectYearly?.();
                }}
                variant="contained"
                sx={{
                  mt: 3,
                  backgroundColor: "#fff",
                  color: "#712d94",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  py: 1.1,
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                {yearlyCtaLabel}
              </Button>
            </CardContent>
          </Card>
        </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const FeatureList = ({ items, tone = "light" }) => (
  <Stack spacing={1.2} sx={{ mt: 2.5 }}>
    {items.map((label) => (
      <Stack key={label} direction="row" spacing={1.2} alignItems="flex-start">
        <CheckCircleIcon
          sx={{
            fontSize: 20,
            color: tone === "dark" ? "#FFC107" : "#712d94",
            mt: 0.25,
            flexShrink: 0,
          }}
        />
        <Typography sx={{ color: tone === "dark" ? "#fff" : "#334155", fontSize: "0.95rem" }}>
          {label}
        </Typography>
      </Stack>
    ))}
  </Stack>
);

export default PricingComparison;
